/**
 * The operator vocabulary — one list, one implementation, five call sites.
 *
 * Before this module there were five: `matchCondition` (delete guards),
 * `condition/evaluate` (client visibility), `utils/compare` (rule validate),
 * `ruleOps/filterItems` and `ruleOps/performOperation`. They disagreed in ways
 * that only showed up when a condition moved between subsystems:
 *
 *   - `compare.js` accepted ONLY `>=` `!==` `===` and threw on every word form;
 *     `performOperation` accepted only word forms and returned false on symbols.
 *   - `equals` was strict equality in `filterItems` and array-coerced membership
 *     in `performOperation`, so the same config meant two different things.
 *
 * ## Spelling
 *
 * camelCase, negation first: `notEquals`, `notIn`, `notExists`, `notEmpty`.
 * The symbolic forms (`>=`, `!==`, `===`) are retired — they were a third
 * spelling of operators that already had names.
 *
 * ## Equality is a parameter, not a fork
 *
 * Guards run against rows read out of JSONB, where Supabase returns `data->>x`
 * as text and `'5' === 5` is false for the wrong reason. Client form state is
 * typed, and there a loose compare would hide real bugs. So `loose` is an
 * explicit option rather than two implementations that quietly differ.
 */

export const OPERATORS = [
    'equals', 'notEquals',
    'in', 'notIn',
    'contains', 'containsAny',
    'exists', 'notExists',
    'empty', 'notEmpty',
    'minLength',
    'gt', 'gte', 'lt', 'lte',
]

/**
 * Operators that take no `value` — they test whether the field holds anything,
 * so a `value` alongside them is meaningless rather than optional.
 */
export const UNARY_OPERATORS = ['exists', 'notExists', 'empty', 'notEmpty']

/** Spellings retired by 30.03.02.G, mapped to what replaced them. */
export const RETIRED_OPERATORS = {
    '===': 'equals',
    '==': 'equals',
    '!==': 'notEquals',
    '!=': 'notEquals',
    '>': 'gt',
    '>=': 'gte',
    '<': 'lt',
    '<=': 'lte',
    not_equals: 'notEquals',
    not_in: 'notIn',
    not_exists: 'notExists',
    not_empty: 'notEmpty',
    notExist: 'notExists',
    isNotNull: 'exists',
    // filterItems' own name for the same set overlap
    intersects: 'containsAny',
}

/** Absent, or a container with nothing in it. `0` and `false` are values. */
export function isEmpty(value) {
    if (value === undefined || value === null) return true
    if (typeof value === 'string') return value.trim() === ''
    if (Array.isArray(value)) return value.length === 0
    if (typeof value === 'object') return Object.keys(value).length === 0
    return false
}

const toArray = (v) => (Array.isArray(v) ? v : v === undefined || v === null ? [] : [v])

const asNumber = (v) => {
    const n = Number(v)
    return Number.isFinite(n) ? n : null
}

const strictEquals = (a, b) => a === b
const looseEquals = (a, b) => {
    if (a === b) return true
    if (a === undefined || a === null || b === undefined || b === null) return false
    return String(a) === String(b)
}

/**
 * Apply one operator.
 *
 * `equals` is strict equality against a single value. Membership is `in`. An
 * array handed to `equals` is a config error rather than an implicit `in` —
 * that conflation is what made the same config mean two things in two engines.
 *
 * Returns `false` for an unknown operator, and says so, because a predicate
 * nobody can evaluate must not silently pass.
 */
export function applyOperator(operator, actual, expected, { loose = false } = {}) {
    const eq = loose ? looseEquals : strictEquals

    switch (operator) {
        case 'equals': return eq(actual, expected)
        case 'notEquals': return !eq(actual, expected)

        case 'in': return toArray(expected).some(v => eq(actual, v))
        case 'notIn': return !toArray(expected).some(v => eq(actual, v))

        // `contains` reads the FIELD as a list — pairs with `lines[].locationId`
        case 'contains': return toArray(actual).some(v => eq(v, expected))
        case 'containsAny': return toArray(actual).some(v => toArray(expected).some(e => eq(v, e)))

        case 'exists': return !(actual === undefined || actual === null || actual === '')
        case 'notExists': return actual === undefined || actual === null || actual === ''

        case 'empty': return isEmpty(actual)
        case 'notEmpty': return !isEmpty(actual)

        case 'minLength':
            return (Array.isArray(actual) || typeof actual === 'string')
                ? actual.length >= expected
                : false

        case 'gt': case 'gte': case 'lt': case 'lte': {
            const a = asNumber(actual)
            const b = asNumber(expected)
            if (a === null || b === null) return false
            if (operator === 'gt') return a > b
            if (operator === 'gte') return a >= b
            if (operator === 'lt') return a < b
            return a <= b
        }

        default: {
            const replacement = RETIRED_OPERATORS[operator]
            console.warn(replacement
                ? `<operators> '${operator}' was retired — use '${replacement}'`
                : `<operators> unknown operator '${operator}'`)
            return false
        }
    }
}
